import { useState, useEffect, useCallback, useMemo } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { MultiSelect } from '@/components/MultiSelect';
import { listHasOtherCompanyItems } from '@/utils/call-card-helpers';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import moment from 'moment';
import { ModuleType } from '@/constants/codes';
import { load } from '@/services/loading';
import { DEFAULT_CALL_CARD_PRIORITY } from '@/constants/priorities';
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const getGeolocation = _wg('getGeolocation');

interface SelfInitiateItem {
  code: number;
  value: string;
  isShownOnMobile: boolean;
  officerStatusLevel: string;
  secondaryNoc: number | null;
}

interface NocItem {
  code: number;
  value: string;
  priority?: number | null;
}

export interface SelfInitiateModalProps {
  open: boolean;
  theme?: string;
  onInitiate?: (id: number, isCleared: boolean) => void;
  onCancel: () => void;
}

export function SelfInitiateModal({
  open,
  theme,
  onInitiate,
  onCancel,
}: SelfInitiateModalProps) {
  const [selectedNocCodes, setSelectedNocCodes] = useState<number[]>([]);
  const [selfInitiateItems, setSelfInitiateItems] = useState<SelfInitiateItem[]>([]);
  const [allNocItems, setAllNocItems] = useState<NocItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isShowingAllNocOptions = localStorage.getItem("IsShowingAllNatureOfCallOptionsOnSelfInitiate") === "true";

  // Track window width for mobile visibility filter (debounced)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setWindowWidth(window.innerWidth), 150);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load data when modal opens (watch on open)
  useEffect(() => {
    if (open) {
      setSelectedNocCodes([]);
      setIsSubmitting(false);
      loadData();
    }
  }, [open]);

  const loadData = useCallback(() => {
    setIsLoading(true);

    // Load self-initiate NOC list (quick buttons)
    api.getSelfInitiateNocList(
      (result: any) => {
        setSelfInitiateItems(
          result
            .map((noc: any) => ({
              code: noc.code,
              value: noc.value,
              isShownOnMobile: noc.isShownOnMobile,
              officerStatusLevel: noc.officerStatusLevel || 'ON_SCENE',
              secondaryNoc: noc.secondaryNoc || null,
            }))
            .sort((a: SelfInitiateItem, b: SelfInitiateItem) =>
              a.value.localeCompare(b.value)
            )
        );
      },
      () => {
        App.showPopupNotification('Error getting self initiate NOC list.', 'error');
      }
    );

    // Load full NOC list for multi-select (only if setting enabled)
    if (localStorage.getItem("IsShowingAllNatureOfCallOptionsOnSelfInitiate") === "true") {
      api.listNatureOfCall(
        (result: any) => {
          setAllNocItems(result.natureOfCallList || []);
          setIsLoading(false);
        },
        () => {
          App.showPopupNotification('Error getting nature of call data.', 'error');
          setIsLoading(false);
        }
      );
    } else {
      setIsLoading(false);
    }
  }, []);

  // Format items for MultiSelect
  const nocSelectItems = useMemo(
    () => allNocItems.map((noc) => ({ label: noc.value, value: noc.code, companyId: (noc as any).companyId, companyName: (noc as any).companyName })),
    [allNocItems]
  );

  // Filter self-initiate items for mobile visibility
  const visibleSelfInitiateItems = useMemo(() => {
    const isMobile = windowWidth <= 768;
    if (!isMobile) return selfInitiateItems;
    return selfInitiateItems.filter((item) => item.isShownOnMobile);
  }, [selfInitiateItems, windowWidth]);

  const createCallCard = useCallback(
    (nocCodes: number[], officerStatusLevel: string = 'ON_SCENE') => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      const cc = new (window as any).CallCard(undefined, ModuleType.mdt, false);
      cc.natureOfCall = nocCodes;

      // Apply NOC-driven priority bump: if any picked NOC has a stronger
      // (lower-numbered) priority than the default, use the strongest.
      const pickedPriorities = nocCodes
        .map((code) => allNocItems.find((n) => n.code === code)?.priority)
        .filter((p): p is number => typeof p === 'number');
      if (pickedPriorities.length > 0) {
        const strongest = Math.min(...pickedPriorities);
        if (strongest < DEFAULT_CALL_CARD_PRIORITY) {
          cc.priority = strongest;
        }
      }

      load.showLoadingScreen();

      const finishSave = (latitude: number, longitude: number) => {
        cc.latitude = latitude;
        cc.longitude = longitude;
        cc.primaryOfficer = JSON.parse(localStorage.AuthToken).user_id;

        if (App.IsNOU(cc.latitude) === false && App.IsNOU(cc.longitude) === false) {
          cc.callDateTime = new Date();
          cc.callLocFormattedAddress = cc.latitude + ',' + cc.longitude;
          cc.callerName = 'N/A';
          cc.callerLocation = 'N/A';

          const dt = new Date(moment().add(-2, 'second').format());
          cc.primaryDispatched = dt;
          if (officerStatusLevel === 'ENROUTE' || officerStatusLevel === 'ON_SCENE' || officerStatusLevel === 'CLEARED') {
            cc.primaryEnroute = dt;
          }
          if (officerStatusLevel === 'ON_SCENE' || officerStatusLevel === 'CLEARED') {
            cc.primaryOnScene = dt;
          }
          if (officerStatusLevel === 'CLEARED') {
            cc.primaryCleared = dt;
          }

          api.saveCallCard(
            cc,
            (result: any) => {
              load.hideLoadingScreen();
              onInitiate?.(result.id, officerStatusLevel === 'CLEARED');
            },
            () => {
              load.hideLoadingScreen();
              App.showPopupNotification('Self initiate failed.', 'error');
              setIsSubmitting(false);
            }
          );
        } else {
          load.hideLoadingScreen();
          setIsSubmitting(false);
        }
      };

      getGeolocation(
        (loc: GeolocationPosition) => finishSave(loc.coords.latitude, loc.coords.longitude),
        () => finishSave(0, 0)
      );
    },
    [isSubmitting, onInitiate, allNocItems]
  );

  const handleInitiate = useCallback(() => {
    if (selectedNocCodes.length === 0) {
      App.showPopupNotification('Please select at least one Nature of Call.', 'error');
      return;
    }
    createCallCard(selectedNocCodes);
  }, [selectedNocCodes, createCallCard]);

  const handleQuickSelect = useCallback(
    (item: SelfInitiateItem) => {
      const nocCodes = [item.code];
      if (item.secondaryNoc != null) {
        nocCodes.push(item.secondaryNoc);
      }
      createCallCard(nocCodes, item.officerStatusLevel);
    },
    [createCallCard]
  );

  return (
    <BaseFormModal
      open={open}
      title="Self Initiate"
      theme={theme}
      dialogClass="kstc-dialog--large kstc-dialog--self-initiate"
      saveText="Initiate"
      showSave={selectedNocCodes.length > 0}
      showCancel={true}
      cancelText="Close"
      onSave={handleInitiate}
      onCancel={onCancel}
    >
      <div className="kst-si-content">
        {/* Multi-select for manual NOC selection (only when all NOC options enabled) */}
        {isShowingAllNocOptions && (
          <div className="kstc-form-group kst-si-select-group">
            <label className="kstc-label">Nature of Call</label>
            <MultiSelect
              value={selectedNocCodes}
              items={nocSelectItems}
              groupBy={listHasOtherCompanyItems(nocSelectItems) ? 'companyName' : null}
              groupOrder="insertion"
              placeholder="Select Nature Of Call..."
              sortItems={true}
              onChange={setSelectedNocCodes}
            />
          </div>
        )}

        {/* Quick-select NOC buttons */}
        <div className="kstc-form-group kst-si-buttons-group">
          <label className="kstc-label">{isShowingAllNocOptions ? 'Or click a button below:' : 'Click a button below:'}</label>
          <div className={`kst-si-buttons${isLoading ? ' kst-si-buttons--loading' : ''}`}>
            {visibleSelfInitiateItems.map((noc) => (
              <button
                key={noc.code}
                type="button"
                className="kst-si-btn"
                disabled={isSubmitting}
                onClick={() => handleQuickSelect(noc)}
              >
                {noc.value}
              </button>
            ))}
            {isLoading && <div className="kst-si-loading"><div className="kst-si-spinner"></div></div>}
            {!isLoading && visibleSelfInitiateItems.length === 0 && (
              <div className="kst-si-empty">No quick-select options configured.</div>
            )}
          </div>
        </div>
      </div>
    </BaseFormModal>
  );
}

(window as any).SelfInitiateModalReact = SelfInitiateModal;
